import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./ProjectOwnership.css";
import ownershipArtwork from "../../../images/integrated-engineering-expertise-visual.png";

type SolutionItem = { title: string; text: string };
type SolutionSection = {
  title: string;
  paragraphs: string[];
  items?: SolutionItem[];
  listTitle?: string;
};
type FrameworkSection = SolutionSection & {
  frameworkTitle?: string;
  principles?: string[];
};
type SolutionConfig = {
  pageTitle: string;
  variant: "team" | "ownership" | "lifecycle" | "productivity" | "realization";
  hero: { title: string; paragraphs: string[]; cta: string };
  overview: SolutionSection;
  framework: FrameworkSection;
  why: SolutionSection;
  cta: { title: string; paragraphs: string[]; cta: string };
};

const config: SolutionConfig = {
  "pageTitle": "Project Ownership & Managed Engineering",
  "hero": {
    "title": "Engineering Ownership. Accountable Delivery.",
    "paragraphs": [
      "As semiconductor programs grow in complexity, organizations often need more than additional engineering capacity—they need a trusted partner to own defined engineering work packages and deliver them with confidence. Successful managed engineering engagements require technical expertise, disciplined execution, transparent governance, and clear accountability throughout the project lifecycle.",
      "Digicomm Semiconductor delivers Project Ownership & Managed Engineering solutions that combine multidisciplinary engineering expertise with structured program management and milestone-driven execution. From planning and execution to quality assurance and delivery, we take ownership of agreed project scope while working collaboratively with your teams to achieve predictable engineering outcomes."
    ],
    "cta": "Discuss Your Project Requirements"
  },
  "overview": {
    "title": "When Engineering Needs Ownership, Not Just Support",
    "paragraphs": [
      "Not every engineering challenge can be addressed by extending an existing team. As project complexity increases, organizations often need a partner who can take complete ownership of defined engineering work packages while ensuring alignment with business objectives, technical requirements, and delivery milestones. This approach enables internal teams to remain focused on strategic priorities while maintaining confidence in execution.",
      "Digicomm Semiconductor's Project Ownership & Managed Engineering model combines experienced multidisciplinary teams with structured project governance and disciplined execution. We take responsibility for agreed project scope, collaborate closely with customer stakeholders, and deliver engineering outcomes through transparent planning, regular progress reviews, and milestone-driven execution."
    ],
    "listTitle": "Ideal Engagement Scenarios",
    "items": [
      {
        "title": "Defined Engineering Work Packages",
        "text": "Delegate complete ownership of clearly defined engineering activities with agreed scope, timelines, and delivery expectations."
      },
      {
        "title": "Complex Multidisciplinary Projects",
        "text": "Execute programs requiring coordinated expertise across multiple semiconductor engineering disciplines under a single delivery framework."
      },
      {
        "title": "Focused Internal Teams",
        "text": "Enable your internal engineering teams to concentrate on strategic initiatives while Digicomm manages execution of assigned project scope."
      },
      {
        "title": "Predictable Project Delivery",
        "text": "Benefit from structured planning, governance, risk management, and milestone-based execution to improve delivery confidence."
      },
      {
        "title": "Scalable Engineering Execution",
        "text": "Expand execution capacity through dedicated managed engineering teams that adapt to changing project priorities while maintaining accountability for outcomes."
      }
    ]
  },
  "framework": {
    "title": "Structured Ownership. Disciplined Execution.",
    "paragraphs": [
      "Successful managed engineering engagements require more than technical expertise—they require a structured framework that ensures clarity, accountability, and consistent execution throughout the project lifecycle. Digicomm's Project Ownership & Managed Engineering model combines experienced engineering teams with defined governance, proactive communication, and milestone-driven delivery to ensure every engagement progresses with confidence.",
      "From project initiation through final delivery, we work collaboratively with customer stakeholders while taking ownership of the agreed engineering scope, enabling transparent execution, effective risk management, and dependable project outcomes."
    ],
    "frameworkTitle": "Our Delivery Framework",
    "items": [
      {
        "title": "Define the Project Scope",
        "text": "We establish clear objectives, technical requirements, deliverables, timelines, responsibilities, and success criteria to ensure complete alignment before execution begins."
      },
      {
        "title": "Build the Right Delivery Team",
        "text": "A multidisciplinary engineering team is assembled based on the project's technical requirements, complexity, and delivery objectives."
      },
      {
        "title": "Execute with Structured Governance",
        "text": "Engineering activities are managed through defined project plans, regular reviews, transparent reporting, and collaborative decision-making to maintain alignment throughout execution."
      },
      {
        "title": "Manage Quality and Delivery",
        "text": "Execution is continuously monitored against agreed milestones, with a strong focus on engineering quality, schedule adherence, risk mitigation, and technical excellence."
      },
      {
        "title": "Deliver with Accountability",
        "text": "Projects conclude with successful delivery of agreed engineering outcomes, supported by documentation, knowledge transfer, and closeout activities that enable a smooth transition to the customer's next phase."
      }
    ],
    "principles": [
      "Clearly Defined Ownership",
      "Structured Project Governance",
      "Transparent Communication",
      "Milestone-Driven Execution",
      "Quality-Focused Delivery",
      "Accountability from Start to Finish"
    ]
  },
  "why": {
    "title": "Why Customers Entrust Engineering Projects to Digicomm",
    "paragraphs": [
      "Successful project ownership requires more than engineering expertise—it demands disciplined execution, transparent governance, and unwavering accountability. Digicomm combines deep semiconductor knowledge with structured delivery methodologies to manage defined engineering work packages with confidence. Every engagement is guided by clear ownership, collaborative decision-making, and a commitment to achieving agreed project objectives."
    ],
    "listTitle": "Our Differentiators",
    "items": [
      {
        "title": "Single Point of Delivery Accountability",
        "text": "A clearly defined ownership structure ensures seamless coordination, effective communication, and consistent accountability throughout the engagement."
      },
      {
        "title": "Structured Project Governance",
        "text": "Regular milestone reviews, transparent reporting, and proactive risk management provide customers with complete visibility into project progress and delivery performance."
      },
      {
        "title": "Multidisciplinary Engineering Excellence",
        "text": "Cross-functional engineering teams collaborate across design, verification, physical design, DFT, validation, software, and product engineering to execute complex programs efficiently."
      },
      {
        "title": "Predictable Execution",
        "text": "Well-defined project planning, disciplined engineering practices, and milestone-driven execution help minimize uncertainty and improve delivery confidence."
      },
      {
        "title": "Quality at Every Stage",
        "text": "Engineering quality is embedded throughout the engagement—from planning and execution to verification, review, and final delivery—ensuring dependable project outcomes."
      },
      {
        "title": "Partnership Beyond Project Delivery",
        "text": "Our engagement extends beyond completing assigned work packages. We build long-term engineering relationships founded on trust, collaboration, and continuous support for future development programs."
      }
    ]
  },
  "cta": {
    "title": "Deliver Your Engineering Projects with Confidence",
    "paragraphs": [
      "When your semiconductor program requires dedicated ownership, disciplined execution, and dependable delivery, Digicomm Semiconductor provides the expertise and governance to manage defined engineering work packages with confidence. From planning and execution to quality assurance and final delivery, we work as your trusted engineering partner—committed to achieving successful project outcomes through structured collaboration and accountable execution."
    ],
    "cta": "Discuss Your Project Requirements"
  },
  "variant": "ownership"
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TechIcon = ({ index = 0 }: { index?: number }) => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    {index % 5 === 0 && <>
      <rect x="13" y="13" width="22" height="22" rx="3" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="19" y="19" width="10" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 7v6M24 7v6M30 7v6M18 35v6M24 35v6M30 35v6M7 18h6M7 24h6M7 30h6M35 18h6M35 24h6M35 30h6" stroke="currentColor" strokeWidth="1.6"/>
    </>}
    {index % 5 === 1 && <>
      <circle cx="24" cy="17" r="6" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="12" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="36" cy="22" r="4" fill="none" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M13 39c1-8 5-12 11-12s10 4 11 12M4 37c1-6 4-9 9-9M44 37c-1-6-4-9-9-9" fill="none" stroke="currentColor" strokeWidth="1.7"/>
    </>}
    {index % 5 === 2 && <>
      <path d="M9 34l9-10 8 6 13-17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M32 13h7v7" fill="none" stroke="currentColor" strokeWidth="2"/>
    </>}
    {index % 5 === 3 && <>
      <path d="M24 6l14 6v10c0 10-6 16-14 20C16 38 10 32 10 22V12z" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M18 24l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
    {index % 5 === 4 && <>
      <circle cx="24" cy="24" r="7" fill="none" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M24 6v6M24 36v6M6 24h6M36 24h6M11 11l4 4M33 33l4 4M37 11l-4 4M15 33l-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </>}
  </svg>
);

const Paragraphs = ({ items }: { items: string[] }) => (
  <>{items.map((p, i) => <p key={i}>{p}</p>)}</>
);

const HeroVisual = () => (
  <div className="sol-hero-visual ownership-hero-artwork">
    <img src={ownershipArtwork} alt="Integrated semiconductor engineering capabilities across the silicon development lifecycle" />
  </div>
);
const FrameworkVisual = () => (
  <div className="ownership-delivery-flow" aria-hidden="true">
    {(config.framework.items || []).map((item, index) => (
      <div className="ownership-delivery-flow__step" key={item.title}>
        <span className="ownership-delivery-flow__number">{String(index + 1).padStart(2, "0")}</span>
        <span className="ownership-delivery-flow__icon"><TechIcon index={index} /></span>
        <strong>{item.title}</strong>
      </div>
    ))}
  </div>
);
const Page: React.FC = () => (
  <Fragment>
    <div className={`solution-experience solution-experience--${config.variant} inner-page`}>
      <div className="body_wrap o-clip">
        <Header />
        <main>
          <PageTitle pageTitle={config.pageTitle} pagesub={config.pageTitle} />

          <section className="sol-hero">
            <div className="sol-shell sol-hero-layout">
              <div className="sol-copy sol-hero-copy">
                <h1>{config.pageTitle}</h1>
                <h2>{config.hero.title}</h2>
                <Paragraphs items={config.hero.paragraphs}/>
                {config.hero.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.hero.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <HeroVisual />
            </div>
          </section>

          <section className="sol-section sol-overview">
            <div className="sol-shell">
              <div className="sol-heading-layout">
                <div className="sol-copy"><h2>{config.overview.title}</h2></div>
                <div className="sol-copy"><Paragraphs items={config.overview.paragraphs}/></div>
              </div>
              {config.overview.listTitle && <h3 className="sol-list-heading">{config.overview.listTitle}</h3>}
              <div className="sol-scenario-grid">
                {(config.overview.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span className="sol-index">{String(i + 1).padStart(2, "0")}</span>
                    <div className="sol-icon"><TechIcon index={i}/></div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="sol-section sol-framework">
            <div className="sol-shell">
              <div className="sol-framework-top">
                <div className="sol-copy">
                  <h2>{config.framework.title}</h2>
                  <Paragraphs items={config.framework.paragraphs}/>
                </div>
                <FrameworkVisual />
              </div>

              {config.framework.frameworkTitle && <h3 className="sol-list-heading">{config.framework.frameworkTitle}</h3>}

              <div className="sol-framework-steps">
                {(config.framework.items || []).map((item, i) => (
                  <article key={item.title}>
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    <div><h3>{item.title}</h3><p>{item.text}</p></div>
                  </article>
                ))}
              </div>

              {!!config.framework.principles?.length && (
                <div className="sol-principles">
                  {config.framework.principles.map((x) => <span key={x}>{x}</span>)}
                </div>
              )}
            </div>
          </section>

          <section className="sol-section sol-why">
            <div className="sol-shell sol-why-layout">
              <div className="sol-copy">
                <h2>{config.why.title}</h2>
                <Paragraphs items={config.why.paragraphs}/>
              </div>

              <div className={`sol-value-system sol-value-system--${config.variant}`}>
                <div className="sol-value-center">
                  <TechIcon index={config.variant === "productivity" ? 4 : config.variant === "team" ? 1 : 0}/>
                  <strong>{config.pageTitle}</strong>
                </div>
                <div className="sol-value-grid">
                  {(config.why.items || []).map((item, i) => (
                    <article key={item.title}>
                      <div className="sol-icon"><TechIcon index={i}/></div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="sol-cta">
            <div className="sol-shell sol-cta-layout">
              <div className="sol-copy">
                <h2>{config.cta.title}</h2>
                <Paragraphs items={config.cta.paragraphs}/>
                {config.cta.cta && (
                  <Link className="sol-btn" to="/contact">
                    {config.cta.cta}<Arrow/>
                  </Link>
                )}
              </div>
              <div className="sol-cta-visual" aria-hidden="true">
                <div className="sol-globe"><i/><i/><i/><i/><i/></div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <Scrollbar />
      </div>
    </div>
  </Fragment>
);

export default Page;
